import isNumber from 'lodash/isNumber';
import type {AutoBrandingSeenServerResponse, BrandKitUpdateServerResponse} from '@Components/auto-branding/auto-branding.types';
import {StyleType} from '@Libraries/ga-events';
import {canUserAccessBrandKits} from '@Libraries/brands-library';
import type {Component, Editor} from 'grapesjs';
import {PALETTE_APPLIED_EVENT, PALETTE_CUSTOM_STYLE} from '@/email-maker/grapesjs-email-editor-plugin/src/custom-style-types/auto-branding';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {ColorPaletteType, getAdminColorPalettesFromRedux, isSameColorPalette} from '@Libraries/color-palettes.library';

const HAS_USER_SEEN_AUTO_BRANDING_CACHE_KEY = 'hasSeenAutoBranding';

export const removeDuplicateColorPalettes = (colorPalettes: ColorPaletteInfo[]): ColorPaletteInfo[] => {
  const uniqueColorPalettes: ColorPaletteInfo[] = [];

  colorPalettes.forEach((colorPalette): void => {
    if (
      !uniqueColorPalettes.some((uniqueColorPalette): boolean => {
        return isSameColorPalette(colorPalette, uniqueColorPalette);
      })
    ) {
      uniqueColorPalettes.push(colorPalette);
    }
  });

  return uniqueColorPalettes;
};

export const changeColorPaletteType = (colorPaletteInfo: ColorPaletteInfo, type: ColorPaletteType): ColorPaletteInfo => {
  return {
    ...colorPaletteInfo,
    type,
  };
};

export interface ColorPaletteInfoToStore {
  colors: string[];
  customizedFromId?: string | number;
  customizedFromType?: ColorPaletteType;
  id?: number;
  name?: string;
}

const createPaletteInfoToStore = (colorPalette: ColorPaletteInfo): string => {
  const infoToStore: ColorPaletteInfoToStore = {
    colors: colorPalette.colors,
  };

  if (colorPalette.type === ColorPaletteType.Recent && colorPalette.id) {
    infoToStore.id = parseInt(colorPalette.id, 10);
    infoToStore.name = colorPalette.name;
  } else {
    infoToStore.customizedFromId = colorPalette.id;
    infoToStore.customizedFromType = colorPalette.type;
    infoToStore.name = colorPalette.name;
  }

  return JSON.stringify(infoToStore);
};

export const storeAppliedPaletteInHtml = (editor: Editor, colorPalette: ColorPaletteInfo): void => {
  const mainWrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;
  const infoToStore = createPaletteInfoToStore(colorPalette);

  mainWrapper.addStyle({[PALETTE_CUSTOM_STYLE]: infoToStore});
  window.dispatchEvent(new CustomEvent(PALETTE_APPLIED_EVENT));
};

export const hasUserSeenAutoBranding = async (): Promise<boolean> => {
  if (!window.PMW.isUserLoggedIn()) {
    return true;
  }

  const cachedHasSeen = getHasSeenAutoBrandingCacheValue();
  if (cachedHasSeen) {
    return cachedHasSeen === 1;
  }

  const data = (await window.PMW.readLocal(`autobrandingemailmaker/hasUserSeenAutoBranding`)) as AutoBrandingSeenServerResponse;
  data.hasSeen = !isNumber(data.hasSeen) ? parseInt(data.hasSeen, 10) : data.hasSeen;
  setHasSeenAutoBrandingCacheValue(data.hasSeen);
  return data.hasSeen === 1;
};

const setHasSeenAutoBrandingCacheValue = (hasSeen: number): void => {
  window.PMW.setCacheItem(HAS_USER_SEEN_AUTO_BRANDING_CACHE_KEY, hasSeen);
};

const getHasSeenAutoBrandingCacheValue = (): number | null => {
  const cachedHasSeen = window.PMW.getCacheItem(HAS_USER_SEEN_AUTO_BRANDING_CACHE_KEY) as number | undefined;

  return cachedHasSeen ?? null;
};

export const markUserSeenAutoBranding = async (): Promise<void> => {
  if (!window.PMW.isUserLoggedIn()) {
    return;
  }

  await window.PMW.writeLocal(`autobrandingemailmaker/markUserHasSeenAutoBranding`);
};

export const updateBrandKitFromColorPalette = async (colorPalette: ColorPaletteInfo): Promise<boolean> => {
  if (!window.PMW.isUserLoggedIn() || !canUserAccessBrandKits()) {
    return false;
  }

  if (!colorPalette.id || colorPalette.colors.length === 0) {
    return false;
  }

  const data = (await window.PMW.writeLocal('autobrandingemailmaker/updateBrandKitFromColorPalette', {
    brandId: colorPalette.id,
    colors: colorPalette.colors,
  })) as BrandKitUpdateServerResponse;
  return data.updateSuccess;
};

export const filterColorPalettesBySearchTerm = (searchTerm: string, colorPalettesToSearchFrom: ColorPaletteInfo[]): ColorPaletteInfo[] => {
  if (colorPalettesToSearchFrom.length === 0) {
    return [];
  }

  const matchesPaletteTag = (palette: ColorPaletteInfo, tag: string): boolean => {
    return (
      palette.tags?.some((paletteTag) => {
        return paletteTag.name.toLowerCase().includes(tag.toLowerCase());
      }) ?? false
    );
  };
  const matchesPaletteName = (palette: ColorPaletteInfo, name: string): boolean => {
    return palette.name?.toLowerCase().includes(name.toLowerCase()) ?? false;
  };

  const filteredColorPalettes = colorPalettesToSearchFrom.filter((palette): boolean => {
    return matchesPaletteName(palette, searchTerm) || matchesPaletteTag(palette, searchTerm);
  });

  return filteredColorPalettes.map((colorPalette): ColorPaletteInfo => {
    return {
      id: colorPalette.id,
      colors: colorPalette.colors,
      tags: colorPalette.tags,
      name: colorPalette.name,
      type: ColorPaletteType.Admin,
    };
  });
};

export const getColorPalettesByTagIds = (tagIds: number[]): ColorPaletteInfo[] => {
  const colorPalettes = getAdminColorPalettesFromRedux();

  if (!colorPalettes) {
    return [];
  }

  const paletteContainsAllOfTags = (palette: ColorPaletteInfo, ids: number[]): boolean => {
    return (
      palette.tags?.every((paletteTag) => {
        return ids.includes(paletteTag.id);
      }) ?? false
    );
  };

  const filteredColorPalettes = colorPalettes.filter((palette): boolean => {
    return paletteContainsAllOfTags(palette, tagIds);
  });

  return filteredColorPalettes.map((colorPalette): ColorPaletteInfo => {
    return {
      id: colorPalette.id,
      colors: colorPalette.colors,
      tags: colorPalette.tags,
      name: colorPalette.name,
      type: ColorPaletteType.Admin,
    };
  });
};

export const getGA4StyleTypeFromColorPalette = (colorPalette: ColorPaletteInfo): string => {
  switch (colorPalette.type) {
    case ColorPaletteType.Admin:
      return StyleType.ALL_STYLES;
    case ColorPaletteType.BrandKit:
      return StyleType.BRAND_KIT;
    case ColorPaletteType.Recent:
      return StyleType.RECENTS;
    default:
      console.error('Unknown color palette type', colorPalette);
      return 'Unknown';
  }
};
